import { TLanguageIDs, TFileId, IItemForGetShortInfoWithStatus, TWordingLevel } from 'types';

export type TTypeOfServiceProvided = 1 | 2 | 3 | 4;
// 1 - Hotels; 2 - Restaurants; 3 - FastFoodRestaurants; 4 - TipsJar (restaurants only);

export enum EntityStatus {
	Active = 'Active',
	Archived = 'Archived',
	Deleted = 'Deleted',
}

export interface IClientType {
	clientTypeID: TTypeOfServiceProvided;
	clientTypeName: string;
}

export interface ICustomerLanguage {
	languageId: TLanguageIDs;
	languageName: string | null;
}

export interface ICustomerRestaurants {
	id: string;
	name: string | null;
}

export interface IItemCustomer {
	id: string;
	name: string | null;
	tips: string | null;
	email: string | null;
	emailBodyTemplate: string | null;
	clientType: TTypeOfServiceProvided;
	ads: number;
	filePath: Nullable<string>;
	defaultLanguageId: string;
	defaultLanguageName: string | null;
	languages: ICustomerLanguage[];
	insertedBy: string | null;
	insertedDate: string;
	updatedBy: Nullable<string>;
	updatedDate: Nullable<string>;
	deletedBy: Nullable<string>;
	deletedDate: Nullable<string>;
	archivedBy: Nullable<string>;
	archivedDate: Nullable<string>;
	restaurants: ICustomerRestaurants[];
	priceType: Nullable<string>;
}

export interface ICustomers {
	customers: IItemCustomer[];
	customerCount: number;
}

export interface ICustomerRestaurantsForGetInfo {
	id: string;
	name: Nullable<string>;
	address: Nullable<string>;
}

export interface ITags {
	languageId: TLanguageIDs;
	tags: string[];
}

export interface ITagsForGetInfo extends ITags {
	languageName: Nullable<string>;
}

export enum CardBrands {
	Visa = 'Visa',
	VisaElectron = 'VisaElectron',
	Mastercard = 'Mastercard',
	Maestro = 'Maestro',
	AMEX = 'AMEX',
	UPI = 'UPI',
	JCB = 'JCB',
	Diners = 'Diners',
	Girocard = 'Girocard',
	Mir = 'Mir',
	CB = 'CB',
}

export interface IMccCard {
	id: string;
	brand: Nullable<CardBrands>;
	mcc: Nullable<number>;
}

export interface IMerchantCategoryCode {
	baseMcc: number;
	mccCards: IMccCard[];
}

export interface IDataGetCustomerInfo {
	id: string;
	name: string | null;
	defaultTips: IDefaultTips[];
	email: string | null;
	emailBodyTemplate: string | null;
	clientType: TTypeOfServiceProvided;
	billTemplatePath: Nullable<string>;
	billAdvertisingImage: Nullable<string>;
	billAdvertisingLink: Nullable<string>;
	priceType: Nullable<string>;
	vivaWalletCredentialsId: Nullable<string>;
	vivaWalletCredentialsName: Nullable<string>;
	ads: number;
	menuVersion: Nullable<string>;
	contentVersion: Nullable<string>;
	mixpanelToken: Nullable<string>;
	stopListSyncIntervalMinutes: number;
	filePath: Nullable<string>;
	defaultLanguageId: string;
	defaultLanguageName: string | null;
	languages: ICustomerLanguage[];
	insertedBy: string | null;
	insertedDate: string;
	updatedBy: Nullable<string>;
	updatedDate: Nullable<string>;
	deletedBy: Nullable<string>;
	deletedDate: Nullable<string>;
	preSignedUrl: Nullable<string>;
	restaurantsCount: number;
	customerRestaurants: ICustomerRestaurantsForGetInfo[];
	tags: ITagsForGetInfo[];
	merchantCategoryCode: IMerchantCategoryCode | null;
	isShowLoyaltyProgramButtons: boolean;
	isActiveBpa: boolean;
}

export interface IGetCustomerInfo {
	data: {
		customer: IDataGetCustomerInfo;
	};
}

export type TCustomersSortBy = 'NameA-Z' | 'NameZ-A' | 'Rest/Hotel1-10' | 'Rest/Hotel10-1';

export interface IGetPaginatedCustomers {
	pageNumber: number;
	pageSize: number;
	searchByName: Nullable<string>;
	status: EntityStatus;
	clientType: null | TTypeOfServiceProvided;
	sortByName: null | 'NameA-Z' | 'NameZ-A';
	sortByRestHotel: null | 'Rest/Hotel1-10' | 'Rest/Hotel10-1';
}

export interface IDefaultTips {
	value: number | null;
	isDefault: boolean;
}

export type TUseTipsByDefault = '0' | '1' | '2' | '3' | null;

export interface ICreateCustomer {
	name: string;
	defaultTips: IDefaultTips[];
	email: Nullable<string>;
	emailBodyTemplate: Nullable<string>;
	clientType: TTypeOfServiceProvided;
	ads: number;
	fileId: TFileId;
	billTemplateId: Nullable<string>;
	billAdvertisingImage: Nullable<string>;
	billAdvertisingLink: Nullable<string>;
	priceType: Nullable<string>;
	mixpanelToken: Nullable<string>;
	stopListSyncIntervalMinutes: number;
	defaultLanguageId: string;
	availableLanguageIds: string[];
}

export interface IMerchantCategoryCodeForUpdate {
	baseMcc: number;
	mccCards: Omit<IMccCard, 'id'>[];
}

export interface IUpdateCustomer extends ICreateCustomer {
	id: string;
	tags: ITags[];
	merchantCategoryCode: IMerchantCategoryCodeForUpdate;
	isShowLoyaltyProgramButtons: boolean;
	menuVersion: Nullable<string>;
	contentVersion: Nullable<string>;
	vivaWalletCredentialsId: Nullable<string>;
	isActiveBpa: boolean;
}

export interface IGetCustomersShort {
	data: {
		customers: IItemForGetShortInfoWithStatus[];
	};
}

export interface ICustomerLocalizationItemTranslation {
	id: string | null;
	languageId: TLanguageIDs;
	value: string | null;
}

export interface ICustomerLocalizationItem {
	id: string | null;
	key: string | null;
	label: string | null;
	level: TWordingLevel;
	translations: ICustomerLocalizationItemTranslation[];
}

export interface ICustomerLocalizationGroup {
	id: string | null;
	name: string | null;
	items: ICustomerLocalizationItem[];
}

export interface ICustomerLocalizationInfo {
	ownerId: string;
	ownerType: 1 | 2;
	useCustomerSettings: boolean;
	groups: ICustomerLocalizationGroup[];
}

export interface IGetCustomerLocalizationInfo {
	data: ICustomerLocalizationInfo;
}

export interface ICreateCustomerLocalizationTranslation {
	label: string | null;
	value: string | null;
	languageId: TLanguageIDs;
}

export interface ICreateCustomerLocalization {
	groupId: string;
	key: string;
	translations: ICreateCustomerLocalizationTranslation[];
	customerId: string;
}

export interface IUpdateCustomerLocalization {
	ownerId: string;
	groups: ICustomerLocalizationGroup[];
}

export interface ICustomerLanguages {
	id: TLanguageIDs;
	name: string;
	isDefault: boolean;
}

export interface IGetCustomerLanguages {
	data: {
		languages: ICustomerLanguages[];
	};
}

export interface IStyleSettings {
	id: string;
	key: string;
	value: string;
}

export interface IStyleSettingColors {
	generalColors: IStyleSettings[];
	additionalColors: {
		[key: string]: {
			[key: string]: IStyleSettings[];
		};
	} | null;
}

export type TMediaFileType = 1 | 2 | 3;
// 1 - Image; 2 - Video; 3 - PDF;

export type TKeyForNewItemToCarousel = 'image_screen_saver' | 'image_how_it_works' | 'image_free_right_button';

export interface IItemsOfGroupsOfMediaSettings {
	id: string;
	translationId: string;
	key: Nullable<string>;
	mediaFileType: TMediaFileType;
	filePath: Nullable<string>;
}

export interface IGroupsOfMediaSettings {
	id: string;
	key: string;
	isMultipleItems: boolean;
	items: IItemsOfGroupsOfMediaSettings[];
}

export interface IMediaSettings {
	languageId: TLanguageIDs;
	groups: IGroupsOfMediaSettings[];
}

export type TAllergensShowCondition = 'GoingToMenu' | 'ChoosingDish';

export interface IDocumentSettings {
	id: string;
	key: Nullable<string>;
	filePath: Nullable<string>;
	termsShowInModal: Nullable<boolean>;
	allergensShowCondition: Nullable<TAllergensShowCondition>;
}

export interface ICustomerStylesInfo {
	ownerId: string;
	styleSettingColors: IStyleSettingColors;
	mediaSettings: IMediaSettings[];
	documentSettings: IDocumentSettings[];
}

export interface IGetCustomerStylesInfo {
	data: ICustomerStylesInfo;
}

export interface IItemsOfGroupsOfMediaSettingsForUpdate {
	id: Nullable<string>;
	translationId: Nullable<string>;
	key: Nullable<string>;
	mediaFileType: TMediaFileType;
	fileId: TFileId;
}

export interface IGroupsOfMediaSettingsForUpdate {
	id: string;
	key: string;
	items: IItemsOfGroupsOfMediaSettingsForUpdate[];
}

export interface IMediaSettingsForUpdate {
	languageId: TLanguageIDs;
	groups: IGroupsOfMediaSettingsForUpdate[];
}

export interface IDocumentSettingsForUpdate {
	id: string;
	key: Nullable<string>;
	fileId: Nullable<string>;
	termsShowInModal: Nullable<boolean>;
	allergensShowCondition: Nullable<TAllergensShowCondition>;
}

export interface IUpdateCustomerStyles {
	ownerId: string;
	styleSettings: IStyleSettings[];
	mediaSettings: IMediaSettingsForUpdate[];
	documentSettings: IDocumentSettingsForUpdate[];
}

export interface ICustomItemsOfGroups {
	id: string;
	translationId: string;
	key: Nullable<string>;
	mediaFileType: TMediaFileType;
	filePath: Nullable<string>;
	sourceUrl: string;
	fileId: TFileId;
}

export interface ICustomGroups {
	id: string;
	key: string;
	isMultipleItems: boolean;
	items: ICustomItemsOfGroups[];
}

export interface ICustomMediaSettingsForUpdate {
	languageId: TLanguageIDs;
	groups: ICustomGroups[];
}

export interface ICustomDocumentSettingsForUpdate {
	id: string;
	key: Nullable<string>;
	filePath: Nullable<string>;
	fileId: Nullable<string>;
	fileName: Nullable<string>;
	termsShowInModal: Nullable<boolean>;
	allergensShowCondition: Nullable<TAllergensShowCondition>;
}

export interface IDataGetCustomersForAssignFirmware {
	id: string;
	name: Nullable<string>;
	filePath: Nullable<string>;
	hasFirmwares: boolean;
}

export interface IGetCustomersForAssignFirmware {
	data: {
		customers: IDataGetCustomersForAssignFirmware[];
	};
}

export interface IDataGetCustomersForAssignApk {
	id: string;
	name: Nullable<string>;
	filePath: Nullable<string>;
	hasTabletApps: boolean;
}

export interface IGetCustomersForAssignApk {
	data: {
		customers: IDataGetCustomersForAssignApk[];
	};
}

export interface IDataGetCustomersForAssignGame {
	id: string;
	name: Nullable<string>;
	filePath: Nullable<string>;
	hasGames: boolean;
}

export interface IGetCustomersForAssignGame {
	data: {
		customers: IDataGetCustomersForAssignGame[];
	};
}

export interface IDataGetCustomersForAssignVideos {
	id: string;
	name: Nullable<string>;
	filePath: Nullable<string>;
	hasVideos: boolean;
}

export interface IGetCustomersForAssignVideos {
	data: {
		customers: IDataGetCustomersForAssignVideos[];
	};
}

export interface IGetPaginatedCustomerSoftwareUpdates {
	customerId: string;
	pageNumber: number;
	pageSize: number;
	searchByName: Nullable<string>;
	sortName: null | 'Name1-10' | 'Name10-1';
	sortLastUpdateDate: null | 'LastUpdate1-10' | 'LastUpdate10-1';
	sortUpdateBy: null | 'UpdatedByA-Z' | 'UpdatedByZ-A';
	sortSoftwareType: null | 'SoftwareTypeA-Z' | 'SoftwareTypeZ-A';
}

export type TArgumentsForGetCustomersShort = Nullable<{ countryId: Nullable<string>; cityId: Nullable<string> }>;

export enum BpaPaymentSystem {
	Tieto = 'Tieto',
	M4Bank = 'M4Bank',
	SBP = 'SBP',
}

export interface ICustomerBPA {
	id: string;
	name: Nullable<string>;
	recipientAccountNumber: Nullable<string>;
	recipientName: Nullable<string>;
	recipientBank: Nullable<string>;
	recipientBankCity: Nullable<string>;
	recipientInn: Nullable<string>;
	recipientBic: Nullable<string>;
	recipientCorrespondentAccount: Nullable<string>;
	paymentDetails: Nullable<string>;
	cardContractNumber: Nullable<string>;
	sbpContractNumber: Nullable<string>;
	tariffs: {
		id: string;
		bpaPaymentSystem: BpaPaymentSystem;
		cardBrand: CardBrands;
		bankFee: Nullable<number>;
		kmFee: Nullable<number>;
	}[];
}

export interface IGetCustomerBPA {
	data: {
		bpaSets: ICustomerBPA[];
	};
}
export interface IExtendedCustomerBPA {
	id: string;
	isOpenSet: boolean;
	name: Nullable<string>;
	recipientAccountNumber: Nullable<string>;
	recipientName: Nullable<string>;
	recipientBank: Nullable<string>;
	recipientBankCity: Nullable<string>;
	recipientInn: Nullable<string>;
	recipientBic: Nullable<string>;
	recipientCorrespondentAccount: Nullable<string>;
	paymentDetails: Nullable<string>;
	cardContractNumber: Nullable<string>;
	sbpContractNumber: Nullable<string>;
	tariffs: {
		id: string;
		isOpenTariff: boolean;
		typeOfBpaPaymentSystem: Nullable<BpaPaymentSystem>;
		data: {
			id: string;
			bpaPaymentSystem: Nullable<BpaPaymentSystem>;
			cardBrand: Nullable<CardBrands>;
			bankFee: Nullable<number>;
			kmFee: Nullable<number>;
		}[];
	}[];
}

export interface ICustomerBPAForUpdate {
	id: Nullable<string>;
	name: string;
	recipientAccountNumber: string;
	recipientName: string;
	recipientBank: string;
	recipientBankCity: string;
	recipientInn: string;
	recipientBic: string;
	recipientCorrespondentAccount: string;
	paymentDetails: string;
	cardContractNumber: string;
	sbpContractNumber: string;
	tariffs: {
		id: Nullable<string>;
		bpaPaymentSystem: Nullable<BpaPaymentSystem>;
		cardBrand: Nullable<CardBrands>;
		bankFee: number;
		kmFee: number;
	}[];
}

export interface IUpdateCustomerBPA {
	customerId: string;
	bpaSets: ICustomerBPAForUpdate[];
}

export interface IBpaShort {
	id: string;
	name: Nullable<string>;
	recipientAccountNumber: Nullable<string>;
	recipientName: Nullable<string>;
	recipientBank: Nullable<string>;
	recipientBankCity: Nullable<string>;
	recipientInn: Nullable<string>;
	recipientBic: Nullable<string>;
	recipientCorrespondentAccount: Nullable<string>;
	paymentDetails: Nullable<string>;
	cardContractNumber: Nullable<string>;
	sbpContractNumber: Nullable<string>;
}

export interface IGetBPAShort {
	data: {
		bpaSets: IBpaShort[];
	};
}
